// ===========================================
// COMPONENTE: ActividadCombobox
// ===========================================
// Combobox con búsqueda para actividades ARCA (F883)

import * as React from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import actividadesData from '@/data/actividades-arca.json'

interface Actividad {
  codigo: string
  descripcion: string
  descripcionLarga: string
}

interface ActividadComboboxProps {
  value: string | null // codigo
  onValueChange: (codigo: string | null, descripcion: string | null) => void
  placeholder?: string
  disabled?: boolean
}

/**
 * Normaliza texto removiendo acentos/diacríticos para búsqueda
 * Ej: "fabricación" -> "fabricacion"
 */
function normalizeText(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
}

export function ActividadCombobox({
  value,
  onValueChange,
  placeholder = 'Buscar actividad...',
  disabled = false,
}: ActividadComboboxProps) {
  const [open, setOpen] = React.useState(false)
  const [searchQuery, setSearchQuery] = React.useState('')

  const actividades = actividadesData as Actividad[]

  // Filtrar por código o descripción (búsqueda sin acentos)
  const filteredActividades = React.useMemo(() => {
    if (!searchQuery) return actividades.slice(0, 50) // Mostrar primeras 50 si no hay búsqueda

    const queryNormalized = normalizeText(searchQuery)
    return actividades
      .filter(
        (a) =>
          a.codigo.includes(searchQuery) ||
          normalizeText(a.descripcion).includes(queryNormalized)
      )
      .slice(0, 100) // Limitar a 100 resultados
  }, [actividades, searchQuery])

  // Encontrar actividad seleccionada
  const selectedActividad = React.useMemo(() => {
    if (!value) return null
    return actividades.find((a) => a.codigo === value) || null
  }, [actividades, value])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal h-auto min-h-10"
        >
          {selectedActividad ? (
            <span className="truncate text-left">
              <span className="text-muted-foreground mr-2 font-mono text-xs">
                {selectedActividad.codigo}
              </span>
              {selectedActividad.descripcion}
            </span>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[500px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar por código o descripción..."
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList className="max-h-none overflow-visible">
            <CommandEmpty>No se encontraron actividades</CommandEmpty>
            <div
              className="max-h-[300px] overflow-y-auto overscroll-contain"
              onWheel={(e) => {
                // Propagar scroll manualmente para touchpad
                const target = e.currentTarget
                target.scrollTop += e.deltaY
              }}
            >
              <CommandGroup>
                {/* Opción para limpiar */}
                {value && (
                  <CommandItem
                    value="__clear__"
                    onSelect={() => {
                      onValueChange(null, null)
                      setOpen(false)
                      setSearchQuery('')
                    }}
                    className="text-muted-foreground"
                  >
                    <span className="italic">Limpiar selección</span>
                  </CommandItem>
                )}
                {filteredActividades.map((actividad) => (
                  <CommandItem
                    key={actividad.codigo}
                    value={actividad.codigo}
                    onSelect={() => {
                      onValueChange(actividad.codigo, actividad.descripcion)
                      setOpen(false)
                      setSearchQuery('')
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === actividad.codigo ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <div className="flex flex-col">
                      <span>
                        <span className="font-mono text-muted-foreground mr-2 text-xs">
                          {actividad.codigo}
                        </span>
                        {actividad.descripcion}
                      </span>
                    </div>
                  </CommandItem>
                ))}
                {filteredActividades.length >= 100 && (
                  <div className="p-2 text-center text-sm text-muted-foreground border-t">
                    Mostrando 100 resultados. Refina tu búsqueda.
                  </div>
                )}
              </CommandGroup>
            </div>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
